import React, { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Search, Edit3, Trash2, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = () => ({ withCredentials: true });

const BLANK = {
  name: "",
  location_name: "",
  location_address: "",
  parking_address: "",
  hospital_name: "",
  hospital_address: "",
  hospital_phone: "",
  hospital_er_notes: "",
  project_ids: [],
};

export default function LocationsManagement() {
  const [locations, setLocations] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | "add" | location object
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const { data } = await axios.get(`${API}/locations`, ax());
      setLocations(data);
    } finally { setLoading(false); }
  };

  const openAdd = () => { setForm(BLANK); setModal("add"); };
  const openEdit = (l) => {
    setForm({ ...BLANK, ...l, project_ids: l.project_ids || [] });
    setModal(l);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return alert("Location name is required");
    setSaving(true);
    try {
      if (modal === "add") {
        const { data } = await axios.post(`${API}/locations`, form, ax());
        setLocations([...locations, data].sort((a, b) => a.name.localeCompare(b.name)));
      } else {
        const { data } = await axios.put(`${API}/locations/${modal.id}`, form, ax());
        setLocations(locations.map(l => l.id === modal.id ? data : l).sort((a, b) => a.name.localeCompare(b.name)));
      }
      setModal(null);
    } catch (e) {
      alert(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this location from your database?")) return;
    await axios.delete(`${API}/locations/${id}`, ax());
    setLocations(locations.filter(l => l.id !== id));
  };

  const filtered = locations.filter(l =>
    !search ||
    (l.name || "").toLowerCase().includes(search.toLowerCase()) ||
    (l.location_address || "").toLowerCase().includes(search.toLowerCase()) ||
    (l.location_name || "").toLowerCase().includes(search.toLowerCase())
  );

  const inputCls = "w-full bg-[#0D0F12] border border-[#28303F] rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500";

  return (
    <div className="p-6 md:p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-amber-400/80 uppercase tracking-widest mb-1 font-mono">Database</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white font-[Outfit] tracking-tight">Company Locations Database</h1>
          <p className="text-gray-500 text-sm mt-1">{locations.length} saved location{locations.length === 1 ? "" : "s"}</p>
        </div>
        <button data-testid="location-add-btn" onClick={openAdd}
          className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-black font-bold px-4 py-2 rounded-lg text-sm transition-all">
          <Plus size={15}/> Add Location
        </button>
      </div>

      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600" />
          <input data-testid="location-search-input" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search name or address..."
            className="w-full bg-[#1A1F29] border border-[#28303F] rounded-lg pl-9 pr-4 py-2.5 text-white placeholder-gray-600 focus:outline-none focus:border-amber-500 text-sm" />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-amber-400 animate-pulse">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-[#28303F] rounded-2xl">
          <MapPin size={36} className="text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400 font-medium">{locations.length === 0 ? "No saved locations yet" : "No results"}</p>
          <p className="text-gray-600 text-sm mt-1">Save frequently-used locations for one-click autofill on call sheets</p>
        </div>
      ) : (
        <>
          <div className="space-y-3 md:hidden">
            {filtered.map((location) => (
              <article
                key={location.id}
                data-testid="location-mobile-card"
                className="border border-[#28303F] bg-[#14181F] p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="font-medium text-white">{location.name}</h2>
                    <p className="mt-1 whitespace-pre-line text-xs text-gray-400">
                      {location.location_address || location.location_name || "—"}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      onClick={() => openEdit(location)}
                      data-testid="location-mobile-edit-btn"
                      className="rounded p-1.5 text-gray-500 transition-colors hover:bg-blue-500/10 hover:text-blue-400"
                      title="Edit"
                    >
                      <Edit3 size={14} />
                    </button>
                    <button
                      onClick={() => del(location.id)}
                      data-testid="location-mobile-delete-btn"
                      className="rounded p-1.5 text-gray-500 transition-colors hover:bg-red-500/10 hover:text-red-400"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <div className="mt-3 border-t border-[#28303F] pt-3">
                  <p className="mb-1 text-[10px] uppercase tracking-wider text-gray-600">Scope</p>
                  <span data-testid="location-company-badge" className="text-xs text-emerald-300">Company record</span>
                </div>
              </article>
            ))}
          </div>
          <div className="hidden overflow-hidden rounded-xl border border-[#28303F] bg-[#14181F] md:block">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#28303F] text-xs text-gray-500 uppercase tracking-wider">
                <th className="text-left px-5 py-3">Name</th>
                <th className="text-left px-4 py-3">Address</th>
                <th className="text-left px-4 py-3">Scope</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l, i) => (
                <tr key={l.id} data-testid="location-table-row"
                  className={`border-b border-[#28303F]/50 hover:bg-[#1A1F29] transition-colors ${i % 2 === 0 ? "" : "bg-[#14181F]/50"}`}>
                  <td className="px-5 py-3 font-medium text-white align-top">{l.name}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs whitespace-pre-line align-top">{l.location_address || l.location_name || "—"}</td>
                  <td className="px-4 py-3 align-top">
                    <span data-testid="location-company-badge" className="text-xs text-emerald-300">Company record</span>
                  </td>
                  <td className="px-4 py-3 text-right align-top">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(l)} data-testid="location-edit-btn"
                        className="p-1.5 text-gray-500 hover:text-blue-400 hover:bg-blue-500/10 rounded transition-all" title="Edit">
                        <Edit3 size={14}/>
                      </button>
                      <button onClick={() => del(l.id)} data-testid="location-delete-btn"
                        className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-all" title="Delete">
                        <Trash2 size={14}/>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </>
      )}

      {modal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#14181F] border border-[#28303F] rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-[#28303F] sticky top-0 bg-[#14181F] z-10">
              <h3 className="font-bold text-white font-[Outfit]">{modal === "add" ? "Add Location" : "Edit Location"}</h3>
              <button onClick={() => setModal(null)} className="text-gray-500 hover:text-white text-2xl leading-none">&times;</button>
            </div>
            <form onSubmit={save} className="p-5 space-y-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">Location Name *</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required
                  placeholder="e.g. The Warehouse, Studio B"
                  data-testid="location-name-input"
                  className={inputCls} />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">Address</label>
                <textarea value={form.location_address} onChange={e => setForm({ ...form, location_address: e.target.value })}
                  placeholder={"123 Main St\nCity, State ZIP"}
                  data-testid="location-address-input"
                  rows={3}
                  className={inputCls + " resize-y font-mono leading-relaxed"} />
                <p className="text-[10px] text-gray-600 mt-1">Press Enter for line breaks — they'll display on the call sheet.</p>
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setModal(null)}
                  className="flex-1 border border-[#28303F] text-gray-400 hover:text-white py-2.5 rounded-lg text-sm transition-colors">Cancel</button>
                <button type="submit" disabled={saving} data-testid="location-save-btn"
                  className="flex-1 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-black font-bold py-2.5 rounded-lg text-sm transition-all">
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
